import _ from 'lodash';
import {createSelector} from 'reselect';

import {areArraysShallowEqual} from '../../../../libs/shared/helpers/src/utilities/arrayHelpers';
import {lazyTeammatesLogger} from '../../../helpers/lazyTeammatesHelpers';
import {hasCommonNamespace, isTeammateActive} from '../../../helpers/modelHelpers';
import {AppParametricSelector} from '../../../helpers/reselectHelpers';
import {TeammateModel} from '../../../models/api/teammateModel';
import {AppState} from '../../appState';
import {getHasLazyTeammates} from '../../featureFlags/featureFlagsSelectors';
import {getTeammates, getUser} from './teammatesSelectors';

/*
 * Types.
 */

export type TeammatesSelector<P> = AppParametricSelector<P, ReadonlyArray<TeammateModel>>;

/*
 * Selectors.
 */

/** Get the map of all lazy teammates. */
export function getLazyTeammates(state: AppState) {
  return state.data.lazyTeammates;
}

export type FindTeammate = (teammateId: number, tag: string) => TeammateModel | undefined;
export const getFindTeammate = createSelector(
  [getTeammates, getLazyTeammates, getHasLazyTeammates],
  (teammates, lazyTeammates, hasLazyTeammates) => (teammateId: number, tag: string) => {
    const teammate = teammates[teammateId];
    if (!hasLazyTeammates) {
      return teammate;
    }

    const lazyTeammate = lazyTeammates[teammateId];
    if (lazyTeammate?.id === teammate?.id) {
      return lazyTeammate;
    }

    lazyTeammatesLogger.warn('Discrepancy in teammate collection', {
      tag,
      teammate_id: teammate?.id,
      lazy_teammate_id: lazyTeammate?.id,
    });
    return teammate;
  },
);

export type FindTeammateByEmail = (teammateEmail: string, tag: string) => TeammateModel | undefined;
export const getFindTeammateByEmail = createSelector(
  [getTeammates, getLazyTeammates, getHasLazyTeammates],
  (teammates, lazyTeammates, hasLazyTeammates) => {
    const teammatesByEmail = _(teammates)
      .values()
      .compact()
      .keyBy((t) => t.email)
      .value();
    const lazyTeammatesByEmail = _(lazyTeammates)
      .values()
      .compact()
      .keyBy((t) => t.email)
      .value();
    return (teammateEmail: string, tag: string) => {
      const teammate = teammatesByEmail[teammateEmail];
      if (!hasLazyTeammates) {
        return teammate;
      }

      const lazyTeammate = lazyTeammatesByEmail[teammateEmail];
      if (lazyTeammate?.id === teammate?.id) {
        return lazyTeammate;
      }

      lazyTeammatesLogger.warn('Discrepancy in teammate collection by email', {
        tag,
        teammate_email: teammateEmail,
        teammate_id: teammate?.id,
        lazy_leammate_id: lazyTeammate?.id,
      });
      return teammate;
    };
  },
);

/** Get a a flat list of all teammates, sorted by name. */
export const getLazyFlatTeammates = createSelector(getLazyTeammates, (teammates) =>
  _(teammates)
    .values()
    .compact()
    .sortBy((t) => (t.givenName + t.familyName).toLowerCase())
    .value(),
);

/** Get a flat list of all active teammates, sorted by name. */
export const getLazyFlatActiveTeammates = createSelector(getLazyFlatTeammates, (teammates) =>
  teammates.filter((t) => isTeammateActive(t)),
);

/** Get a flat list of all teammates except our user. */
export const getLazyFlatOtherActiveTeammates = createSelector(
  [getUser, getLazyFlatActiveTeammates],
  (user, teammates) => teammates.filter((t) => t !== user),
);

/** Get a flat list of all teammates with a team in common with our user. */
export const getLazyTeammatesInCommonTeam = createSelector(
  [getUser, getLazyFlatOtherActiveTeammates],
  (user, teammates) => teammates.filter((t) => hasCommonNamespace(user, t)),
);

/** Allows testing for whether lazy teammates works for a given selector. */
export function makeGetTeammateListWithLazyValidation<P>(
  makeGetSelectorToValidate: (getTeammates: TeammatesSelector<P>) => TeammatesSelector<P>,
  getFullTeammatesInput: TeammatesSelector<P>,
  getLazyTeammatesInput: TeammatesSelector<P>,
  tag: string,
) {
  const getFullTeammatesOutput = makeGetSelectorToValidate(getFullTeammatesInput);
  const getLazyTeammatesOutput = makeGetSelectorToValidate(getLazyTeammatesInput);
  return createSelector(
    [getFullTeammatesOutput, getLazyTeammatesOutput, getHasLazyTeammates],
    (fullTeammatesOutput, lazyTeammatesOutput, hasLazyTeammates) => {
      if (!hasLazyTeammates) {
        return fullTeammatesOutput;
      }

      const areArraysEqual = areArraysShallowEqual(
        fullTeammatesOutput,
        lazyTeammatesOutput,
        (t1, t2) => t1.id === t2.id,
      );
      if (areArraysEqual) {
        return lazyTeammatesOutput;
      }

      lazyTeammatesLogger.warn('Discrepancy in teammate list', {
        tag,
        full_teammate_ids: fullTeammatesOutput.map((t) => t.id),
        lazy_teammate_ids: lazyTeammatesOutput.map((t) => t.id),
      });
      return fullTeammatesOutput;
    },
  );
}
